<template>
  <div class="tooth-table">
    <div class="tooth-table__head">
      <h5 class="mb-0">{{ $t('AddedTreatments') }}</h5>
      <span class="badge badge-pill iq-bg-primary">{{ $t('TotalTreatmentCount') }}: {{ details.length }}</span>
    </div>

    <div class="table-responsive">
      <table class="table table-hover mb-0">
        <thead>
          <tr>
            <th>{{ $t('Tooth') }}</th>
            <th class="w-100">{{ $t('Treatment') }}</th>
            <th>{{ $t('Quantity') }}</th>
            <th>{{ $t('Staff') }}</th>
            <th v-if="showPricing" class="text-right">{{ $t('Price') }}</th>
            <th>{{ $t('ToothNote') }}</th>
            <th class="text-right">{{ $t('Actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="group in groups" :key="group.key"
              @mouseenter="$emit('hover-group', group.toothNumbers)"
              @mouseleave="$emit('hover-group', [])">
            <td>
              <div class="tooth-table__chips">
                <span v-for="item in group.items" :key="item.tempId || item.id"
                      class="tooth-chip tooth-chip--removable"
                      @mouseenter="$emit('hover-tooth', item.toothNumber)"
                      @mouseleave="$emit('hover-tooth', null)">
                  {{ item.toothNumber }}
                  <button type="button" class="tooth-chip__x" :title="$t('Delete')" :aria-label="$t('Delete')" @click.stop="$emit('delete-tooth', item)">
                    <i class="fa fa-times" aria-hidden="true"></i>
                  </button>
                </span>
              </div>
            </td>
            <td class="tooth-table__entity">
              <i :class="group.entityType === 'product' ? 'fa fa-box' : 'fa fa-tooth'" class="mr-1 text-primary" aria-hidden="true"></i>
              {{ group.entityName }}
            </td>
            <td class="text-nowrap">{{ group.quantityText }}</td>
            <td class="text-nowrap">{{ group.staffName }}</td>
            <td v-if="showPricing" class="text-right text-nowrap font-weight-bold">{{ formatAmount(group.totalPrice) }} {{ group.currencySymbol }}</td>
            <td><note-preview v-if="group.toothNotes" :note="group.toothNotes" /></td>
            <td class="text-right">
              <b-button variant="link" size="sm" class="text-danger p-0" :title="$t('Delete')" :aria-label="$t('Delete')" @click="confirmDeleteGroup(group)">
                <i class="fa-regular fa-trash-can" aria-hidden="true"></i>
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="showPricing && currencyTotals.length > 0" class="tooth-table__totals">
      <template v-if="currencyTotals.length > 1">
        <span v-for="total in currencyTotals" :key="total.currencyId" class="tooth-table__subtotal">
          {{ formatAmount(total.amount) }} {{ total.symbol }}
        </span>
      </template>
      <span class="tooth-table__total">
        <span>{{ $t('TreatmentPrice') }}</span>
        <strong>{{ formatAmount(mainTotal) }} {{ mainCurrencySymbol }}</strong>
      </span>
    </div>
  </div>
</template>

<script>
  import NotePreview from '../shared/NotePreview'
  import { parseAmount, formatAmount, convertToMainCurrency, confirmToothAction, TOOTH_TREATMENT_PRICING_ENABLED } from './toothConfig'
  import '../../plugins/sweetalert'
  import './tooth-chip.css'

  export default {
    name: 'ToothTreatmentTable',
    components: { NotePreview },
    props: {
      details: { type: Array, required: true },
      parityList: { type: Array, default: () => [] },
      showPricing: { type: Boolean, default: TOOTH_TREATMENT_PRICING_ENABLED }
    },
    computed: {
      mainCurrencyId() {
        return (this.$store.getters['Auth/tenantInfo'] || {}).mainCurrencyId
      },
      mainCurrencySymbol() {
        return (this.$store.getters['Auth/tenantInfo'] || {}).mainCurrencySymbol || ''
      },
      // Aynı hizmet/ürün + personel + kur tek satırda toplanır (liste ucuyla aynı anahtar).
      groups() {
        const grouped = {}
        this.details.forEach(detail => {
          const entityId = detail.serviceId || detail.productId
          const key = `${detail.entityType}_${entityId}_${detail.staffId}_${detail.currencyId}`
          if (!grouped[key]) {
            grouped[key] = {
              key,
              entityType: detail.entityType,
              entityName: detail.serviceName || detail.productName,
              staffName: detail.staffName,
              currencySymbol: detail.currencySymbol,
              toothNumbers: [],
              quantities: [],
              totalPrice: 0,
              toothNotes: '',
              items: []
            }
          }
          const group = grouped[key]
          group.items.push(detail)
          group.toothNumbers.push(detail.toothNumber)
          group.quantities.push(parseAmount(detail.quantity))
          group.totalPrice += parseAmount(detail.rowTotal)
          if (detail.toothNotes && group.toothNotes.split('; ').indexOf(detail.toothNotes) === -1)
            group.toothNotes = group.toothNotes ? `${group.toothNotes}; ${detail.toothNotes}` : detail.toothNotes
        })

        return Object.values(grouped).map(group => {
          group.items.sort((a, b) => a.toothNumber - b.toothNumber)
          group.toothNumbers.sort((a, b) => a - b)
          const distinct = group.quantities.filter((q, i, list) => list.indexOf(q) === i)
          // Dişlerin hepsinde aynı miktar varsa "x2", değilse toplam miktar gösterilir.
          group.quantityText = distinct.length === 1 ? `x${distinct[0]}` : `${group.quantities.reduce((s, q) => s + q, 0)}`
          return group
        }).sort((a, b) => a.toothNumbers[0] - b.toothNumbers[0])
      },
      currencyTotals() {
        const totals = {}
        this.details.forEach(detail => {
          if (!totals[detail.currencyId]) totals[detail.currencyId] = { currencyId: detail.currencyId, symbol: detail.currencySymbol, amount: 0 }
          totals[detail.currencyId].amount += parseAmount(detail.rowTotal)
        })
        return Object.values(totals).filter(t => t.amount !== 0)
      },
      mainTotal() {
        return this.currencyTotals.reduce((sum, t) => sum + convertToMainCurrency(t.amount, t.currencyId, this.mainCurrencyId, this.parityList), 0)
      }
    },
    methods: {
      formatAmount,
      confirmDeleteGroup(group) {
        confirmToothAction(this, { text: this.$t('DeleteGroupConfirmationText'), danger: true }).then(confirmed => {
          if (confirmed) this.$emit('delete-group', group)
        })
      }
    }
  }
</script>

<style scoped>
  .tooth-table__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .tooth-table .table th {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .02em;
    color: #777d74;
    border-top: 0;
    white-space: nowrap;
  }

  .tooth-table .table td {
    vertical-align: middle;
  }

  .tooth-table__entity {
    min-width: 180px;
    color: #374948;
    font-weight: 500;
  }

  .tooth-table__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    min-width: 132px;
  }


  .tooth-chip__x {
    display: inline-flex;
    padding: 0;
    margin-left: 2px;
    border: 0;
    background: transparent;
    color: inherit;
    font-size: 10px;
    visibility: hidden;
    cursor: pointer;
  }

  .tooth-chip--removable:hover {
    background: #fd6256;
  }

  .tooth-chip--removable:hover .tooth-chip__x {
    visibility: visible;
  }

  .tooth-table__totals {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: baseline;
    gap: 16px;
    padding-top: 12px;
    margin-top: 4px;
    border-top: 1px solid #eef2f3;
  }

  .tooth-table__subtotal {
    color: #777d74;
  }

  .tooth-table__total {
    display: inline-flex;
    align-items: baseline;
    gap: 10px;
    color: #374948;
  }

  .tooth-table__total strong {
    font-size: 18px;
    color: #089bab;
  }
</style>
